/**
 * API Client for NEST Pay
 */
import type { PaymentLink, PaymentInitiateResponse, ApiError } from '@nest-pay/shared'

function resolveApiBase(): string {
  const configuredBase = import.meta.env.VITE_API_URL?.trim()
  if (configuredBase) {
    return configuredBase.replace(/\/$/, '')
  }

  if (typeof window === 'undefined') {
    return ''
  }

  switch (window.location.hostname) {
    case 'pay.nestrentalapartments.com':
      return 'https://nest-pay-api.imbaworkers.workers.dev'
    case 'staging.pay.nestrentalapartments.com':
      return 'https://nest-pay-api-staging.imbaworkers.workers.dev'
    default:
      // In development, Vite proxies /api to localhost:8787.
      return ''
  }
}

const API_BASE = resolveApiBase()

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  })

  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `HTTP ${response.status}`)
  }

  return response.json()
}

export const api = {
  /**
   * Get payment link by short code
   */
  getPaymentLink: (shortCode: string): Promise<PaymentLink> => {
    return fetchApi<PaymentLink>(`/api/payment/${shortCode}`)
  },

  /**
   * Initiate payment transaction
   */
  initiatePayment: (shortCode: string): Promise<PaymentInitiateResponse> => {
    return fetchApi<PaymentInitiateResponse>(`/api/payment/${shortCode}/initiate`, {
      method: 'POST',
    })
  },
}
